import type { Locale, Translate } from "@khazzin/i18n";
import { formatRelative, type Instant } from "@khazzin/i18n/dates";
import { AlertTriangle, Bell, Info, OctagonAlert } from "lucide-react";
import { useState } from "react";
import { Icon } from "../icons/Icon";
import { Badge } from "../primitives/Display";
import { Popover } from "../primitives/Menu";
import { cx, FOCUS_RING } from "../styles";

/**
 * The bell: an unread count, and the ten most recent things that happened.
 *
 * **It takes its feed as an argument.** Each app hands it the inbox source
 * (EP12-S03, `useInboxSource`); the Driver App, whose alerts are pushes, hands
 * it a quiet one. The shape below is the *contract*, `{ unreadCount, latest,
 * status }`, rather than a query key.
 *
 * **Ten, and a link to the rest.** `10/05` §7 fixes the number, and the noise
 * budget in §3 is the reason: a dropdown that can hold everything becomes the
 * place notifications go to be ignored. What is here is what happened since you
 * last looked; the full history is the inbox page.
 *
 * **Severity is an icon and a tone, never a tone alone.** Somebody who cannot
 * distinguish amber from red still has to be able to tell a warning from a
 * failure, so each row carries a shape as well as a colour.
 */

/** How many rows the dropdown shows before deferring to the full page. */
export const FEED_LIMIT = 10;

/** Above this the badge says `9+` rather than a number nobody reads as a number. */
export const BADGE_CAP = 9;

/** Where "View all" goes. */
export const INBOX_PATH = "/inbox";

export type NotificationSeverity = "info" | "action_needed" | "critical";

/**
 * One notification, as the bell needs it. The title is already in the
 * reader's language: the server renders the in-app template in it.
 */
export interface NotificationItem {
	id: string;
	topic: string;
	severity: NotificationSeverity;
	title: string;
	/** Above 1 for a storm coalesced into one row (`10/05` §9). */
	count?: number;
	/** Where clicking the row goes. A path in this app, never an absolute URL. */
	deepLink?: string;
	/** Null when it has not been read. */
	readAt?: string | null;
	createdAt: string;
}

export interface NotificationFeed {
	unreadCount: number;
	latest: NotificationItem[];
	/**
	 * Whether the feed itself is healthy. A failure is quiet: the badge keeps
	 * the last count it knew, muted, and nothing else changes.
	 */
	status: "loading" | "ready" | "error";
}

export interface NotificationSource {
	useFeed: () => NotificationFeed;
	/** Absent for a source that cannot; the button renders disabled. */
	markAllRead?: () => Promise<void>;
	/** Marks one row read as it is opened. */
	markRead?: (id: string) => Promise<void>;
}

export interface NotificationBellProps {
	source: NotificationSource;
	t: Translate;
	/** For the relative times. The same locale the rest of the shell is in. */
	locale: Locale;
	/** An IANA name, from `preferences.timezone`. */
	timeZone: string;
	/** Called with a row's `deepLink`. The shell passes its router's navigate. */
	onNavigate?: (to: string) => void;
	/** Fixed in tests, so "3 minutes ago" does not depend on when the suite runs. */
	now?: Instant;
}

export const SEVERITY: Record<NotificationSeverity, { icon: typeof Info; className: string }> = {
	info: { icon: Info, className: "text-info-fg" },
	action_needed: { icon: AlertTriangle, className: "text-warning-fg" },
	critical: { icon: OctagonAlert, className: "text-danger-fg" },
};

/** A row's title, with the size of its storm when there was one. */
export function titleOf(item: Pick<NotificationItem, "title" | "count">, t: Translate): string {
	const count = item.count ?? 1;
	return count > 1
		? t("ui.notifications.coalesced", { title: item.title, more: count - 1 })
		: item.title;
}

export function NotificationBell({
	source,
	t,
	locale,
	timeZone,
	onNavigate,
	now,
}: NotificationBellProps) {
	const feed = source.useFeed();
	const [open, setOpen] = useState(false);

	const unread = Math.max(0, feed.unreadCount);
	const rows = feed.latest.slice(0, FEED_LIMIT);
	const stale = feed.status === "error";

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			align="end"
			// Phone width: the popover spans the screen less its margins, which
			// is the bottom sheet's width without a second component.
			className="w-[360px] max-w-[calc(100vw-2rem)] p-0"
			trigger={
				<button
					type="button"
					// The count is in the name, not only in the badge: "Notifications"
					// alone tells a screen-reader user nothing about whether it is
					// worth opening, which is the entire question the badge answers.
					aria-label={t("ui.notifications.label", { count: unread })}
					className={cx(
						// 44px on a phone, as every topbar control there (ADJ-M01-1).
						"relative grid size-8 shrink-0 place-items-center rounded-sm border border-border max-sm:size-11",
						"text-fg-muted hover:bg-bg-subtle hover:text-fg",
						FOCUS_RING,
					)}
				>
					<Icon as={Bell} size={18} aria-hidden="true" />
					{unread > 0 && (
						<Badge
							// Muted while the feed cannot be read: the last count it
							// knew, said quietly (`10/05` §8).
							tone={stale ? "neutral" : "accent"}
							// Ringed in the surface colour so the badge reads as a badge
							// rather than as part of the icon it overlaps.
							className="absolute -top-1.5 -end-1.5 border-2 border-bg-surface"
						>
							{/* `9+` above nine: three digits in a seventeen-pixel pill is
							    a smear, and the difference between 47 and 112 unread is
							    not a difference anybody acts on differently. */}
							{unread > BADGE_CAP ? `${BADGE_CAP}+` : String(unread)}
						</Badge>
					)}
				</button>
			}
		>
			<div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
				<span className="text-[13px] font-bold text-fg">
					{t("ui.notifications.title", { count: unread })}
				</span>
				<button
					type="button"
					disabled={source.markAllRead === undefined || unread === 0}
					onClick={() => void source.markAllRead?.()}
					className={cx(
						"rounded-sm px-1.5 py-0.5 text-[12px] font-semibold text-brand-ink max-sm:min-h-12",
						"hover:bg-brand-subtle disabled:cursor-not-allowed disabled:text-fg-subtle",
						FOCUS_RING,
					)}
				>
					{t("ui.notifications.markAll")}
				</button>
			</div>

			{rows.length === 0 ? (
				// Quiet, in both senses. A feed that failed to load says the same
				// thing as one with nothing in it, because `10/05` §8 says a failure
				// here surfaces on the connection pill and nowhere else — a toast
				// about the notification list is itself notification noise.
				<p className="px-3 py-6 text-center text-[12.5px] text-fg-subtle">
					{t("ui.notifications.empty")}
				</p>
			) : (
				<ul className="max-h-80 overflow-y-auto" data-scroll="1">
					{rows.map((item) => {
						const tone = SEVERITY[item.severity] ?? SEVERITY.info;
						const unreadRow = item.readAt === null || item.readAt === undefined;

						return (
							<li key={item.id}>
								<button
									type="button"
									onClick={() => {
										setOpen(false);
										if (unreadRow) {
											void source.markRead?.(item.id);
										}
										if (item.deepLink !== undefined) {
											onNavigate?.(item.deepLink);
										}
									}}
									className={cx(
										"flex w-full items-start gap-2.5 px-3 py-2 text-start hover:bg-bg-subtle",
										FOCUS_RING,
									)}
								>
									<Icon
										as={tone.icon}
										size={15}
										aria-hidden="true"
										className={cx("mt-0.5 shrink-0", tone.className)}
									/>
									<span className="min-w-0 flex-1">
										<span
											className={cx(
												"block truncate text-[12.5px] text-fg",
												unreadRow && "font-bold",
											)}
										>
											{titleOf(item, t)}
										</span>
										<span className="block text-[11px] text-fg-subtle">
											{formatRelative(item.createdAt, {
												locale,
												timeZone,
												...(now === undefined ? {} : { now }),
											})}
										</span>
									</span>
									{unreadRow && (
										<span
											aria-hidden="true"
											className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent"
										/>
									)}
								</button>
							</li>
						);
					})}
				</ul>
			)}

			<div className="border-t border-border px-3 py-2">
				<button
					type="button"
					onClick={() => {
						setOpen(false);
						onNavigate?.(INBOX_PATH);
					}}
					className={cx(
						"w-full rounded-sm py-1 text-[12.5px] font-semibold text-brand-ink hover:bg-brand-subtle max-sm:min-h-12",
						FOCUS_RING,
					)}
				>
					{t("ui.notifications.viewAll")}
				</button>
			</div>
		</Popover>
	);
}
